import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { authPost, authFetch } from '../../lib/authFetch';
import './MobileProfile.css';
import './SwipeBack.css';

// Word reveal animation component - matches desktop DonorPanel
function WordReveal({ text, animate = true }) {
  const [visibleWords, setVisibleWords] = useState(animate ? 0 : Infinity);
  const words = text.split(/(\s+)/);
  const actualWords = words.filter(w => w.trim());
  const totalWords = actualWords.length;

  useEffect(() => {
    if (!animate) {
      setVisibleWords(Infinity);
      return;
    }
    setVisibleWords(0);
    const interval = setInterval(() => {
      setVisibleWords(prev => {
        if (prev >= totalWords) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 30);
    return () => clearInterval(interval);
  }, [text, animate, totalWords]);

  let wordIndex = 0;
  return (
    <span className="word-reveal-text">
      {words.map((word, i) => {
        if (!word.trim()) {
          return <span key={i}>{word}</span>;
        }
        const isVisible = wordIndex < visibleWords;
        wordIndex++;
        return (
          <span key={i} className={`reveal-word ${isVisible ? 'visible' : ''}`}>
            {word}
          </span>
        );
      })}
    </span>
  );
}

function MobileProfile({ isOpen, onClose, donorName, donorData, onSubmitNote, onAskQuestion, onNavigateToPerson, dataMode = 'real', narrativeUpdate }) {
  const [noteText, setNoteText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetchedData, setFetchedData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isResearching, setIsResearching] = useState(false);
  const [newResearchText, setNewResearchText] = useState(null);
  const [newNoteText, setNewNoteText] = useState(null);
  const [narrativeUpdated, setNarrativeUpdated] = useState(false);
  const [feedUpdateNarrative, setFeedUpdateNarrative] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [researchOptions, setResearchOptions] = useState(null);
  const [researchMessage, setResearchMessage] = useState(null);
  const [isConfirmationMode, setIsConfirmationMode] = useState(false); // true = ready to save, false = disambiguation
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [documents, setDocuments] = useState([]);
  const researchInProgressRef = useRef(false);
  const previousNarrativeRef = useRef(null);
  const previousNarrativeForNoteRef = useRef(null);
  const swipeStartX = useRef(0);
  const swipeStartY = useRef(0);
  const isHorizontalSwipe = useRef(null);

  // Swipe to close handling
  const handleTouchStart = useCallback((e) => {
    swipeStartX.current = e.touches[0].clientX;
    swipeStartY.current = e.touches[0].clientY;
    isHorizontalSwipe.current = null;
    setIsSwiping(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isSwiping) return;

    const deltaX = e.touches[0].clientX - swipeStartX.current;
    const deltaY = e.touches[0].clientY - swipeStartY.current;

    // Determine swipe direction
    if (isHorizontalSwipe.current === null && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      isHorizontalSwipe.current = Math.abs(deltaX) > Math.abs(deltaY);
    }

    // Only handle horizontal swipes going right
    if (isHorizontalSwipe.current && deltaX > 0) {
      setSwipeX(deltaX);
    }
  }, [isSwiping]);

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping) return;

    // Close if swiped more than 100px
    if (swipeX > 100) {
      setSwipeX(window.innerWidth);
      setTimeout(() => {
        onClose();
        setSwipeX(0);
      }, 200);
    } else {
      setSwipeX(0);
    }

    setIsSwiping(false);
    isHorizontalSwipe.current = null;
  }, [isSwiping, swipeX, onClose]);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 450); // Match animation duration
  }, [onClose]);

  // Handle narrative updates from Feed
  useEffect(() => {
    if (narrativeUpdate?.narrative) {
      // Store the current narrative before the update
      const currentNarrative = fetchedData?.narrative || '';
      previousNarrativeRef.current = currentNarrative;

      // Set the new narrative for word reveal animation
      setFeedUpdateNarrative(narrativeUpdate.narrative);
      setNarrativeUpdated(true);

      // Refresh data to get the update
      setRefreshKey(prev => prev + 1);

      // Clear animation after it plays
      const wordCount = narrativeUpdate.narrative.split(/\s+/).length;
      const animationDuration = Math.max(wordCount * 30 + 1000, 3000);
      setTimeout(() => {
        setFeedUpdateNarrative(null);
        setNarrativeUpdated(false);
      }, animationDuration);
    }
  }, [narrativeUpdate]);

  // Track previous donor name to know when we're switching people vs refreshing
  const prevDonorNameRef = useRef(donorName);

  // Fetch donor data from Supabase
  useEffect(() => {
    // Only clear data when switching to a DIFFERENT person, not when refreshing
    const isSwitchingPerson = prevDonorNameRef.current !== donorName;
    if (isSwitchingPerson) {
      setFetchedData(null);
      setResearchOptions(null);
      setResearchMessage(null);
      setIsConfirmationMode(false);
      setNewResearchText(null);
      prevDonorNameRef.current = donorName;
    }

    const fetchDonor = async () => {
      if (!donorName || !isOpen) return;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { data, error } = await supabase
          .from('donors')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('name', donorName)
          .single();

        if (!error && data) {
          const newData = {
            id: data.id,
            amount: data.amount,
            narrative: data.narrative,
            lastContact: data.last_contact,
            stage: data.stage,
            nextMeeting: data.next_meeting,
            nextTask: data.next_task,
            tasks: data.tasks || [],
            timelineEvents: data.timeline_events || [],
            keyDetails: data.key_details || []
          };

          setFetchedData(newData);

          // Always fetch timeline - server returns cached if available
          if (data.narrative) {
            try {
              const response = await authPost('/api/donor-timeline', {
                donorName,
                mode: dataMode
              });
              if (response.ok) {
                const timelineData = await response.json();
                // Update with timeline, keyDetails, and tasks from API
                setFetchedData(prev => ({
                  ...prev,
                  timelineEvents: timelineData.timeline?.length > 0 ? timelineData.timeline : prev.timelineEvents,
                  keyDetails: timelineData.keyDetails?.length > 0 ? timelineData.keyDetails : prev.keyDetails,
                  tasks: timelineData.tasks?.length > 0 ? timelineData.tasks : prev.tasks
                }));
              }
            } catch (timelineErr) {
              console.error('Error fetching timeline:', timelineErr);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching donor:', err);
      }
    };

    fetchDonor();
  }, [donorName, isOpen, refreshKey, dataMode]);

  // Fetch documents when profile opens
  useEffect(() => {
    if (!isOpen || !fetchedData?.id) return;
    const fetchDocuments = async () => {
      try {
        const response = await authFetch(`/api/documents/${fetchedData.id}`);
        if (response.ok) {
          const data = await response.json();
          setDocuments(data.documents || []);
        }
      } catch (err) {
        console.error('Error fetching documents:', err);
      }
    };
    fetchDocuments();
  }, [isOpen, fetchedData?.id]);

  // Always prefer fetched data (most up-to-date from DB)
  const effectiveData = fetchedData || donorData || {};

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get avatar color based on name (cool colors only)
  const getAvatarColor = (name) => {
    const colors = ['#a78bfa', '#60a5fa', '#4ade80', '#2dd4bf', '#818cf8'];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  // Format relative date
  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '';

    // If it's a natural language date without a year (like "April 1" or "next Tuesday"),
    // just display it as-is instead of trying to parse it
    const hasYear = /\b20\d{2}\b/.test(dateStr) || /^\d{4}-\d{2}-\d{2}/.test(dateStr);
    const isISOFormat = /^\d{4}-\d{2}-\d{2}/.test(dateStr);

    // Only try to parse if it looks like a proper date format with a year
    if (!hasYear && !isISOFormat) {
      return dateStr; // Return natural language dates as-is
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    // Sanity check: if the parsed year is more than 2 years in the past, something went wrong
    const now = new Date();
    const yearDiff = now.getFullYear() - date.getFullYear();
    if (yearDiff > 2 || yearDiff < -2) return dateStr; // Return as-is if date seems wrong

    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    // Future dates
    if (diffDays < 0) {
      const futureDays = Math.abs(diffDays);
      if (futureDays === 0) return 'Today';
      if (futureDays === 1) return 'Tomorrow';
      if (futureDays < 7) return `In ${futureDays}d`;
      if (futureDays < 30) return `In ${Math.floor(futureDays / 7)}w`;
      return `In ${Math.floor(futureDays / 30)}mo`;
    }

    // Past dates
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  };

  // Get event type color
  const getEventColor = (event) => {
    const text = (event?.event || '').toLowerCase();
    if (text.includes('meeting') || text.includes('coffee') || text.includes('lunch')) return '#4ade80';
    if (text.includes('call') || text.includes('phone')) return '#60a5fa';
    if (text.includes('gala') || text.includes('event')) return '#a78bfa';
    return '#666';
  };

  // Extract key details (interests, family, etc.) from narrative
  const extractKeyDetails = (narrativeText) => {
    if (!narrativeText) return [];
    const details = [];
    const text = narrativeText.toLowerCase();

    // Interest patterns
    const interests = ['arts', 'education', 'music', 'sports', 'athletics', 'science', 'research',
                       'scholarship', 'building', 'campus', 'library', 'technology', 'stem',
                       'youth', 'children', 'health', 'medical', 'environmental', 'sustainability'];
    interests.forEach(interest => {
      if (text.includes(interest)) {
        details.push({ label: interest.charAt(0).toUpperCase() + interest.slice(1), type: 'interest' });
      }
    });

    // Family patterns
    const familyPatterns = [
      /(?:wife|husband|spouse)\s+(\w+)/i,
      /(?:daughter|son)\s+(\w+)/i,
      /(?:brother|sister)\s+(\w+)/i,
    ];
    familyPatterns.forEach(pattern => {
      const match = narrativeText.match(pattern);
      if (match) {
        const relation = pattern.toString().includes('wife') ? 'Wife' :
                        pattern.toString().includes('husband') ? 'Husband' :
                        pattern.toString().includes('daughter') ? 'Daughter' :
                        pattern.toString().includes('son') ? 'Son' :
                        pattern.toString().includes('brother') ? 'Brother' : 'Sister';
        details.push({ label: `${relation}: ${match[1]}`, type: 'family' });
      }
    });

    // Giving patterns
    const amountMatch = narrativeText.match(/\$[\d,]+[kKmM]?/);
    if (amountMatch) {
      details.push({ label: amountMatch[0], type: 'giving' });
    }

    // Limit to 6 items and remove duplicates
    const unique = details.filter((item, index, self) =>
      index === self.findIndex(t => t.label === item.label)
    );
    return unique.slice(0, 6);
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Highlight narrative text with person links and keyword highlights
  const highlightNarrative = (text) => {
    if (!text) return null;

    const interestKeywords = ['arts', 'education', 'music', 'sports', 'athletics', 'science', 'research',
                              'scholarship', 'building', 'campus', 'library', 'technology', 'stem',
                              'youth', 'children', 'health', 'medical', 'environmental', 'sustainability'];
    const familyKeywords = ['wife', 'husband', 'spouse', 'daughter', 'son', 'brother', 'sister', 'family'];
    const allKeywords = [...interestKeywords, ...familyKeywords];

    // First, extract [[Person Name]] links
    const personLinkPattern = /\[\[([^\]]+)\]\]/g;
    const segments = [];
    let lastIndex = 0;
    let linkMatch;

    while ((linkMatch = personLinkPattern.exec(text)) !== null) {
      if (linkMatch.index > lastIndex) {
        segments.push({ text: text.slice(lastIndex, linkMatch.index), type: 'text' });
      }
      segments.push({ text: linkMatch[1], type: 'personLink' });
      lastIndex = linkMatch.index + linkMatch[0].length;
    }
    if (lastIndex < text.length) {
      segments.push({ text: text.slice(lastIndex), type: 'text' });
    }

    // Now process each segment
    const result = [];
    segments.forEach((segment, segIdx) => {
      if (segment.type === 'personLink') {
        // Render as clickable link
        result.push(
          <span
            key={`link-${segIdx}`}
            className="narrative-person-link"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              if (onNavigateToPerson) {
                onNavigateToPerson(segment.text);
              }
            }}
          >
            {segment.text}
          </span>
        );
      } else {
        // Apply keyword highlighting to text segments
        const combinedPattern = new RegExp(
          `(\\$[\\d,]+[kKmM]?|\\b\\d{1,3}(?:,\\d{3})+(?:[kKmM])?\\b|\\b\\d+[kKmM]\\b|\\b(?:${allKeywords.join('|')})\\b)`,
          'gi'
        );

        const parts = [];
        let partLastIndex = 0;
        let match;
        const segText = segment.text;

        while ((match = combinedPattern.exec(segText)) !== null) {
          if (match.index > partLastIndex) {
            parts.push({ text: segText.slice(partLastIndex, match.index), type: 'normal' });
          }

          const matchText = match[1];
          let type = 'normal';
          if (matchText.startsWith('$') || /^\d/.test(matchText)) {
            type = 'amount';
          } else if (interestKeywords.some(k => k.toLowerCase() === matchText.toLowerCase())) {
            type = 'interest';
          } else if (familyKeywords.some(k => k.toLowerCase() === matchText.toLowerCase())) {
            type = 'family';
          }

          parts.push({ text: matchText, type });
          partLastIndex = match.index + matchText.length;
        }

        if (partLastIndex < segText.length) {
          parts.push({ text: segText.slice(partLastIndex), type: 'normal' });
        }

        parts.forEach((part, i) => {
          if (part.type === 'normal') {
            result.push(<span key={`${segIdx}-${i}`}>{part.text}</span>);
          } else {
            result.push(
              <span key={`${segIdx}-${i}`} className={`narrative-highlight ${part.type}`}>
                {part.text}
              </span>
            );
          }
        });
      }
    });

    return result;
  };

  // Check if text is a question
  const isQuestion = (text) => {
    const trimmed = text.trim().toLowerCase();
    const questionWords = ['who', 'what', 'when', 'where', 'why', 'how', 'is', 'are', 'do', 'does', 'can', 'could', 'would', 'should', 'tell me'];
    return trimmed.endsWith('?') || questionWords.some(w => trimmed.startsWith(w + ' '));
  };

  // Handle note submission
  const handleSubmitNote = async () => {
    if (!noteText.trim() || isSubmitting) return;

    // If it's a question, redirect to Feed
    if (isQuestion(noteText) && onAskQuestion) {
      const question = noteText;
      setNoteText('');
      onAskQuestion(question, donorName);
      return;
    }

    setIsSubmitting(true);

    // Store current narrative before update
    previousNarrativeForNoteRef.current = fetchedData?.narrative || effectiveData?.narrative || '';

    try {
      await onSubmitNote(noteText, donorName);
      setNoteText('');

      // Refresh profile data after a short delay to let the server process
      setTimeout(async () => {
        // Fetch updated data
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data } = await supabase
            .from('donors')
            .select('narrative')
            .eq('user_id', session.user.id)
            .eq('name', donorName)
            .single();

          if (data?.narrative && data.narrative !== previousNarrativeForNoteRef.current) {
            // Find the new text that was added
            const prevNarrative = previousNarrativeForNoteRef.current || '';
            const newNarrative = data.narrative;

            // The new text is what's different
            if (newNarrative.startsWith(prevNarrative)) {
              setNewNoteText(newNarrative.slice(prevNarrative.length).trim());
            } else {
              setNewNoteText(newNarrative);
            }
            setNarrativeUpdated(true);

            // Clear animation after it plays
            setTimeout(() => {
              setNewNoteText(null);
              setNarrativeUpdated(false);
            }, 3000);
          }
        }
        setRefreshKey(prev => prev + 1);
      }, 1500);
    } catch (error) {
      console.error('Error submitting note:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle research - fetch web info and add to profile
  // Uses the same /api/research-person endpoint as Add Person modal for consistent results
  const handleResearch = async () => {
    if (!donorName || researchInProgressRef.current) return;

    researchInProgressRef.current = true;
    setIsResearching(true);
    setResearchOptions(null);
    setResearchMessage(null);
    setIsConfirmationMode(false);
    // Store current narrative before research
    previousNarrativeRef.current = fetchedData?.narrative || effectiveData?.narrative || '';

    try {
      // Use the same endpoint as Add Person modal - it returns full info for each match
      const response = await authPost('/api/research-person', {
        personName: donorName,
        existingInfo: previousNarrativeRef.current ? `Existing notes: ${previousNarrativeRef.current}` : ''
      });

      if (response.ok) {
        const data = await response.json();

        // Handle matches - each match has full info ready to save
        if (data.matches && data.matches.length > 0) {
          // Convert to our options format - use 'info' as description (full research text)
          const options = data.matches.map(match => ({
            name: match.name,
            summary: match.summary,
            description: match.info // Full research text, ready to save
          }));
          setResearchOptions(options);
          setResearchMessage(options.length > 1 ? 'Select the right person:' : 'Add this to profile?');
          setIsConfirmationMode(true); // All options have full info, ready to save on click
          return;
        }

        // Handle no matches
        if (data.notFound) {
          setResearchMessage(data.reason || 'No information found.');
          setTimeout(() => setResearchMessage(null), 3000);
          return;
        }

        setResearchMessage('No new information found.');
        setTimeout(() => setResearchMessage(null), 3000);
      } else {
        setResearchMessage('Research failed. Please try again.');
        setTimeout(() => setResearchMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error researching donor:', error);
      setResearchMessage('Research failed. Please try again.');
      setTimeout(() => setResearchMessage(null), 3000);
    } finally {
      researchInProgressRef.current = false;
      setIsResearching(false);
    }
  };

  // Handle selecting a specific person from ambiguous options
  const handleResearchOptionSelect = async (option) => {
    if (isConfirmationMode) {
      // Confirmation mode - save the insight directly with animation
      setResearchOptions(null);
      setResearchMessage(null);
      setIsConfirmationMode(false);
      previousNarrativeRef.current = fetchedData?.narrative || effectiveData?.narrative || '';

      try {
        const insight = option.description || option.insight;
        console.log('[Research Save] Saving insight for', donorName, '- insight:', insight?.substring(0, 100));

        // Check for empty insight
        if (!insight || !insight.trim()) {
          console.error('[Research Save] Empty insight, cannot save');
          setResearchMessage('No research data to save.');
          setTimeout(() => setResearchMessage(null), 3000);
          return;
        }

        // Append the insight to the narrative
        const currentNarrative = previousNarrativeRef.current;
        const updatedNarrative = currentNarrative
          ? currentNarrative + '\n\n' + insight
          : insight;

        // Save via donor-update API
        const updateResponse = await authPost('/api/donor-update', {
          donorName,
          field: 'narrative',
          value: updatedNarrative,
          mode: dataMode
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json().catch(() => ({}));
          console.error('[Research Save] API error:', updateResponse.status, errorData);
          setResearchMessage('Failed to save. Please try again.');
          setTimeout(() => setResearchMessage(null), 3000);
          return;
        }

        console.log('[Research Save] Successfully saved research for', donorName);

        // Store the new research text for word reveal animation
        setNewResearchText(insight);

        // Regenerate timeline with the updated narrative
        try {
          const timelineResponse = await authPost('/api/donor-timeline', {
            donorName,
            mode: dataMode,
            forceRefresh: true
          });
          if (timelineResponse.ok) {
            const timelineData = await timelineResponse.json();
            setFetchedData(prev => ({
              ...prev,
              narrative: updatedNarrative,
              timelineEvents: timelineData.timeline?.length > 0 ? timelineData.timeline : prev?.timelineEvents || [],
              keyDetails: timelineData.keyDetails?.length > 0 ? timelineData.keyDetails : prev?.keyDetails || [],
              tasks: timelineData.tasks?.length > 0 ? timelineData.tasks : prev?.tasks || []
            }));
          }
        } catch (timelineErr) {
          console.error('Error regenerating timeline:', timelineErr);
        }

        // Refresh data after short delay to ensure cache is invalidated
        setTimeout(() => {
          setRefreshKey(prev => prev + 1);
        }, 300);

        // Clear the animation after it completes (estimate based on word count)
        const wordCount = insight.split(/\s+/).length;
        const animationDuration = wordCount * 30 + 500; // 30ms per word + buffer
        setTimeout(() => {
          setNewResearchText(null);
        }, animationDuration);
      } catch (error) {
        console.error('[Research Save] Error:', error);
        setResearchMessage('Failed to save. Please try again.');
        setTimeout(() => setResearchMessage(null), 3000);
      }
    }
  };

  // Get timeline events - prefer effectiveData (from DB) over donorData (prop)
  const timelineEvents = effectiveData?.timelineEvents || effectiveData?.timeline_events || donorData?.timelineEvents || donorData?.timeline_events || [];

  if (!isOpen && !isClosing) return null;

  return (
    <div className={`mobile-profile-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`mobile-profile ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: swipeX > 0 ? `translateX(${swipeX}px)` : 'none',
          transition: isSwiping ? 'none' : 'transform 0.2s ease-out'
        }}
      >
        {/* Swipe back indicator */}
        {swipeX > 20 && (
          <div
            className="swipe-back-indicator"
            style={{
              opacity: Math.min(swipeX / 100, 1),
              transform: `translateX(${Math.min(swipeX * 0.3, 40)}px)`
            }}
          >
            ‹
          </div>
        )}

        {/* Close button */}
        <button className="mobile-profile-close" onClick={handleClose}>
          <span className="close-arrow">↓</span>
        </button>

        {/* Scrollable content */}
        <div className="mobile-profile-content">
          {/* Header */}
          <div className="profile-header">
            <div
              className="profile-avatar profile-avatar-small"
              style={{ borderColor: getAvatarColor(donorName), color: getAvatarColor(donorName) }}
            >
              {getInitials(donorName)}
            </div>
            <h1 className="profile-name">{donorName}</h1>
            {donorData?.title && (
              <p className="profile-title">{effectiveData.title}</p>
            )}
            {donorData?.organization && (
              <p className="profile-org">{effectiveData.organization}</p>
            )}
            <button
              className={`profile-research-btn ${isResearching ? 'loading' : ''}`}
              onClick={() => handleResearch()}
              disabled={isResearching || researchOptions}
            >
              {isResearching ? 'Researching...' : 'Research'}
            </button>
            {researchMessage && !researchOptions && (
              <p className="research-message">{researchMessage}</p>
            )}
            {researchOptions && (
              <div className="research-options">
                <p className="research-options-label">{researchMessage}</p>
                {researchOptions.map((option, idx) => (
                  <button
                    key={idx}
                    className="research-option-btn"
                    onClick={() => handleResearchOptionSelect(option)}
                  >
                    <span className="research-option-name">{option.name}</span>
                    <span className="research-option-desc">{option.summary || option.description}</span>
                  </button>
                ))}
                <button
                  className="research-option-cancel"
                  onClick={() => { setResearchOptions(null); setResearchMessage(null); setIsConfirmationMode(false); }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* The Story */}
          <div className="profile-section">
            <div className="section-label">The Story</div>
            <div className={`profile-story ${narrativeUpdated ? 'narrative-updated' : ''}`}>
              {feedUpdateNarrative ? (
                // Show Feed update with word reveal animation (full new narrative)
                <WordReveal text={feedUpdateNarrative} animate={true} />
              ) : newResearchText ? (
                // Show previous narrative + new research with word reveal
                <>
                  {previousNarrativeRef.current && (
                    <span>{highlightNarrative(previousNarrativeRef.current)}<br /><br /></span>
                  )}
                  <WordReveal text={newResearchText} animate={true} />
                </>
              ) : newNoteText ? (
                // Show previous narrative + new note with word reveal
                <>
                  {previousNarrativeForNoteRef.current && (
                    <span>{highlightNarrative(previousNarrativeForNoteRef.current)}<br /><br /></span>
                  )}
                  <WordReveal text={newNoteText} animate={true} />
                </>
              ) : (
                // Normal display - static text with highlighting
                effectiveData?.narrative ? (
                  highlightNarrative(effectiveData.narrative)
                ) : (
                  <span className="profile-story-empty">No story yet. Add notes from the input below.</span>
                )
              )}
            </div>
          </div>

          {/* Key Details - prefer saved from DB, fallback to local extraction */}
          {((effectiveData?.keyDetails?.length > 0) || extractKeyDetails(effectiveData?.narrative).length > 0) && (
            <div className="profile-section">
              <div className="section-label">Key Details</div>
              <div className="profile-key-details">
                {(effectiveData?.keyDetails?.length > 0 ? effectiveData.keyDetails : extractKeyDetails(effectiveData?.narrative)).map((detail, idx) => (
                  <span
                    key={idx}
                    className={`key-detail-tag ${detail.type || 'default'}`}
                  >
                    {detail.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Timeline */}
          {timelineEvents.length > 0 && (
            <div className="profile-section">
              <div className="section-label">Timeline</div>
              <div className="profile-timeline">
                <div className="timeline-line" />
                {timelineEvents.slice(0, 10).map((event, idx) => (
                  <div key={idx} className="timeline-entry">
                    <div
                      className="timeline-dot"
                      style={{ background: getEventColor(event) }}
                    />
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className="timeline-title">{event.event}</span>
                        <span className="timeline-date">{formatRelativeDate(event.date)}</span>
                      </div>
                      {event.detail && (
                        <p className="timeline-detail">{event.detail}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* To Do */}
          {((effectiveData?.tasks && effectiveData.tasks.length > 0) ||
            (effectiveData?.nextTask && effectiveData.nextTask !== 'No tasks')) && (
            <div className="profile-section">
              <div className="section-label">To Do</div>
              {/* Show tasks array first */}
              {effectiveData?.tasks?.map((task, idx) => (
                <div key={idx} className="profile-todo-item">
                  <div className="todo-checkbox" />
                  <div className="todo-content">
                    <div className="todo-text">{task.text || task}</div>
                    {task.due && <div className="todo-due">{formatRelativeDate(task.due)}</div>}
                  </div>
                </div>
              ))}
              {/* Fallback to nextTask if no tasks array */}
              {(!effectiveData?.tasks || effectiveData.tasks.length === 0) &&
               effectiveData?.nextTask && effectiveData.nextTask !== 'No tasks' && (
                <div className="profile-todo-item">
                  <div className="todo-checkbox" />
                  <div className="todo-content">
                    <div className="todo-text">{effectiveData.nextTask}</div>
                    {effectiveData.nextMeeting && effectiveData.nextMeeting !== 'Not scheduled' && (
                      <div className="todo-due">{formatRelativeDate(effectiveData.nextMeeting)}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Documents */}
          {documents.length > 0 && (
            <div className="profile-section">
              <div className="section-label">Documents</div>
              <div className="profile-documents">
                {documents.map(doc => (
                  <div key={doc.id} className="document-item">
                    <div className="document-icon">
                      {doc.file_type?.includes('pdf') ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <path d="M9 15h6M9 11h6" />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <path d="M16 13H8M16 17H8M10 9H8" />
                        </svg>
                      )}
                    </div>
                    <div className="document-info">
                      <div className="document-name">{doc.file_name}</div>
                      <div className="document-meta">
                        {formatFileSize(doc.file_size)}
                        {doc.ai_summary && (
                          <span className="document-summary">{doc.ai_summary}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Spacer */}
          <div style={{ height: '120px' }} />
        </div>

        {/* Fixed bottom input bar */}
        <div className={`profile-input-bar ${isSubmitting ? 'loading' : ''}`}>
          <div className="profile-input-container">
            <input
              type="text"
              className="profile-input-field"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={`Note about ${donorName?.split(' ')[0]}...`}
              onKeyDown={(e) => e.key === 'Enter' && noteText.trim() && handleSubmitNote()}
              disabled={isSubmitting}
            />
            <button
              className={`profile-input-send ${noteText.trim() ? 'active' : ''}`}
              onClick={handleSubmitNote}
              disabled={!noteText.trim() || isSubmitting}
            >
              <span>↑</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MobileProfile;
